<template>
  <section
    :class="$style.container"
    :style="sectionStyle"
    data-luko-tracking="GivebackSchema"
  >
    <Title
      :class="$style.black_title"
      :title-params="primary.black_title"
      is-extra-title
    />
    <RichText
      v-if="primary.sub_description && primary.sub_description.length"
      :class="$style.sub_description"
      :text-params="primary.sub_description"
    />
    <RichText
      v-if="primary.top_description && primary.top_description.length"
      :class="$style.top_description"
      :text-params="primary.top_description"
    />
    <template v-if="isAnimated">
      <ul :class="[[$style.medium], { [$style.is_playing]: isPlaying }]">
        <li>
          <div :class="[$style.first_description, $style.text_container]">
            <RichText
              :text-params="primary['70__description']"
              :class="$style.description"
            />
            <img src="/images/giveback/line-70.png" :class="$style.line" />
          </div>
        </li>
        <li>
          <video
            v-if="primary.animation_video_mp4"
            ref="video-giveback"
            :class="$style.video"
            muted="muted"
          >
            <source :src="primary.animation_video_mp4.url" type="video/mp4" />
          </video>
        </li>
        <li>
          <div
            v-observe-visibility="{
              callback: visibilityChanged,
              once: false,
            }"
            :class="$style.text_container"
          >
            <div :class="$style.second_description">
              <RichText
                :text-params="primary['30__description']"
                :class="$style.description"
              />
              <img src="/images/giveback/line-30.png" :class="$style.line" />
            </div>
            <div :class="$style.third_description">
              <RichText
                :text-params="primary.remaining_description"
                :class="$style.description"
              />
              <img
                src="/images/giveback/line-remaining.png"
                :class="$style.line"
              />
            </div>
          </div>
        </li>
      </ul>
    </template>

    <ul :class="$style.small">
      <li>
        <RichText :text-params="primary['30__description']" />
      </li>
      <li>
        <RichText :text-params="primary['70__description']" />
      </li>
      <li>
        <RichText :text-params="primary.remaining_description" />
      </li>
    </ul>
    <LkButton v-if="primary.cta_url" is-success :href="primary.cta_url">
      {{ primary.cta_title }}
    </LkButton>
    <RichText
      v-if="primary.sub_cta"
      :class="$style.sub_cta"
      :text-params="primary.sub_cta"
    />
  </section>
</template>

<script>
import RichText from '~/components/RichText'
import Title from '~/components/Title'

export default {
  name: 'GivebackSchema',
  components: {
    RichText,
    Title,
  },
  props: {
    slice: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isPlaying: false,
      primary: this.slice.primary,
    }
  },
  computed: {
    sectionStyle() {
      const backgroundImage = this.primary?.background_image?.url
        ? `background-image:url(${this.primary?.background_image?.url})`
        : ''

      const backgroundColor = this.primary.background_color
        ? `background-color: ${this.primary.background_color};`
        : ''
      return `${backgroundColor} ${backgroundImage}`
    },
    isAnimated() {
      return !!this.primary.animation_video_mp4
    },
  },
  methods: {
    visibilityChanged(isVisible, entry) {
      if (isVisible) {
        this.$refs['video-giveback'].currentTime = 0
        this.$refs['video-giveback'].play()
        this.isPlaying = true
      } else {
        this.isPlaying = false
      }
    },
  },
}
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 80px 0 0;
  padding: 0 20px 80px 20px;

  background: linear-gradient(180deg, rgba(235, 245, 243, 0) 15%, #ebf5f3 100%);

  background-repeat: no-repeat;

  background-position: bottom;
  background-size: contain;
}

.black_title {
  @include below(small) {
    text-align: center;
  }
}

.sub_description {
  @include typo-body;
  max-width: 840px;
  margin-bottom: 96px;

  text-align: center;

  p {
    margin-top: 16px;
    margin-bottom: 20px;
  }
}

.top_description {
  @include typo-headline;
  color: $mint-700;
  text-align: center;

  p {
    line-height: 32px;
  }

  strong {
    color: $mint-900;
  }

  @include below(medium) {
    display: none;
  }
}

ul.medium {
  display: flex;
  max-width: 1280px;
  min-height: 400px;

  li:nth-child(2) {
    width: 400px;
  }
  li:nth-child(1),
  li:nth-child(3) {
    width: 25vw;
  }

  @include below(medium) {
    display: none;
  }
}

.text_container {
  position: relative;

  height: 100%;
}

.description,
.line {
  transform: translate(0, 80px);
  opacity: 0;
}

.description {
  color: $mint-700;
  strong {
    color: $mint-900;
  }
}

.first_description {
  text-align: right;

  .description {
    position: absolute;
    right: 30px;
    bottom: 28%;
  }
  .line {
    position: absolute;
    bottom: 13%;
  }
}

.second_description {
  .description {
    position: absolute;
    bottom: 50%;
    left: 30px;

    transform: translate(0, 80px);
  }
  .line {
    position: absolute;
    bottom: 50%;
  }
}

.third_description {
  .description {
    position: absolute;
    bottom: 13%;
    left: 30px;
  }
  .line {
    position: absolute;
    bottom: 15%;
  }
}

.sub_cta {
  margin-top: 10px;
  p {
    color: $mint-600;
    font-weight: 500;
    font-size: 24px;
    line-height: 40px;
  }
}

ul.small {
  display: flex;
  flex-direction: column;

  li {
    max-width: 323px;
    margin: 0;
    padding: 16px 0 16px 16px;
    border-left: 4px solid $mint-300;
    &:nth-child(2) {
      border-left: 4px solid $mint-200;
    }
    &:nth-child(3) {
      border-left: 4px solid $mint-100;
    }
    p {
      font-size: 16px;
      line-height: 24px;
      text-align: left;
    }
  }
  @include above(medium) {
    display: none;
  }
}

.is_playing {
  .description,
  .line {
    transform: translate(0, 0);
    opacity: 1;
  }

  .first_description {
    .description,
    .line {
      transition: opacity 0.8s 2.9s, transform 0.8s 2.9s;
    }
  }

  .second_description {
    .description,
    .line {
      transition: opacity 0.8s 1.3s, transform 0.8s 1.3s;
    }
  }
  .third_description {
    .description,
    .line {
      transition: opacity 0.8s 4.5s, transform 0.8s 4.5s;
    }
  }
}

.description,
.line {
  transition: opacity 0s 0s, transform 0s 0s;
}

.video {
  width: 100%;
}
</style>
